import React, { Fragment, useState } from 'react';
import { PaperAirplaneIcon, UserIcon } from '@heroicons/react/20/solid';
import { useSelector } from 'react-redux';
import { Dialog, Transition } from '@headlessui/react';
import { RootState } from '../../../app/store';
import { ContactStatus } from '../profileTypes';
import { classNames } from '../../../helpers/styling';
import {
  useAddContactMutation,
  useCancelContactRequestMutation,
  useRemoveContactMutation,
} from '../profilePageAPI';
import {
  useAcceptContactRequestMutation,
} from '../../contact-list/contactListAPI';
import Textarea from '../../../elements/Textarea';
import Button from '../../../elements/Button';
import {
  RulesForFieldsType, RuleTypes, useFormValidation, ValidationError,
} from '../../../helpers/validation';

type ValidationKeys = 'message';

const rulesForFields: RulesForFieldsType<ValidationKeys> = {
  message: {
    userFriendlyFieldName: 'Message',
    ruleType: RuleTypes.text,
    required: true,
    minLength: 10,
    maxLength: 500,
  },
};

const validationErrorState: ValidationError<ValidationKeys> = {
  message: { errorMessage: '' },
};

// todo: delete request - waiting for the backend
export default function ProfileContactButton() {

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');

  const [validationState, validate, onValidationInputChange] = useFormValidation<ValidationKeys>(
    rulesForFields,
    validationErrorState,
    {
      message,
    },
  );

  const {
    allowPageEdit,
    profileOfCurrentUser: { requesterContactStatus, name },
    currentlyLoadedProfileID,
  } = useSelector((state: RootState) => state.profilePage);

  const { currentProfile: { profileID } } = useSelector((state: RootState) => state.account);

  const [addContactQuery, { isLoading: isAddingContactInProgress }] = useAddContactMutation();
  const [cancelContactRequestQuery] = useCancelContactRequestMutation();
  const [removeContactQuery] = useRemoveContactMutation();
  const [acceptContactRequestQuery] = useAcceptContactRequestMutation();

  const onFormSubmit = async () => {
    if (! currentlyLoadedProfileID || ! profileID) return;

    const val = validate();

    if (! val.allValid) { return; }

    await addContactQuery({
      message,
      receiverProfilePageId: currentlyLoadedProfileID,
      senderProfilePageId: profileID,
    });

    setMessage('');
    setOpen(false);
  };

  const onAddContactButton = () => {
    if (! currentlyLoadedProfileID || ! profileID) return;

    if (requesterContactStatus === ContactStatus.none) {
      setOpen(true); // opens modal

      return;
    }

    if (requesterContactStatus === ContactStatus.accepted) {
      removeContactQuery({
        receiverProfilePageId: currentlyLoadedProfileID,
        senderProfilePageId: profileID,
      });
    }

    if (requesterContactStatus === ContactStatus.pending) {
      cancelContactRequestQuery({
        receiverProfilePageId: currentlyLoadedProfileID,
        senderProfilePageId: profileID,
      });
    }

    if (requesterContactStatus === ContactStatus.received) {
      acceptContactRequestQuery({
        receiverProfilePageId: profileID,
        senderProfilePageId: currentlyLoadedProfileID,
      });
    }
  };

  if (allowPageEdit) {
    return null;
  }

  let text = 'Add to contacts';
  if (requesterContactStatus === ContactStatus.pending) {
    text = 'Cancel contact request'; // Unsend contact request
  }

  if (requesterContactStatus === ContactStatus.accepted) {
    text = 'Remove contact';
  }

  if (requesterContactStatus === ContactStatus.received) {
    text = 'Accept contact request';
  }

  return (
    <>
      <button
        type="button"
        onClick={() => onAddContactButton()}
        className={classNames(
          'inline-flex items-center justify-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white focus:outline-none',
          // `${disabled ? 'opacity-75 pointer-events-none' : 'hover:bg-neutral-50'}`,
        )}
      >
        <UserIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
        <span>{text}</span>
      </button>

      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={setOpen}>
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6">
                <p className="font-semibold text-left pb-4">
                  Send contact request to
                  {' '}
                  {name}
                  :
                </p>
                <p className="text-xs font-semibold text-neutral-600 pl-2 mb-1">
                  Message:
                </p>
                <Textarea
                  value={message}
                  placeholder="Type your message..."
                  onChange={(e) => {
                    setMessage(e.target.value);
                    onValidationInputChange('message');
                  }}
                  validationMessage={validationState.message.errorMessage}
                />

                <div className="pt-4 flex justify-end">
                  <Button
                    onClick={onFormSubmit}
                    size="medium"
                    iconLeft={<PaperAirplaneIcon className="w-4 h-4 transform rotate-45 mr-2 hover:rotate-90 transition-transform" />}
                    description="Send request"
                    isLoading={isAddingContactInProgress}
                  />
                </div>
              </div>

            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
